import ALL_ACCOMMODATION_QUALIFICATIONS from '@/graphql/accommodation-qualifications/query/getAllAccommodationQualifications.gql'

const accommodationQualifications = ref<{label: string; value: string}[]>([])
const key = ref(0)
const firstTime = ref(true)

export async function accommodationQualificationsAutocomplete(params: any) {
  const apolloQuery = ALL_ACCOMMODATION_QUALIFICATIONS
  const variables = {
    search: '%' + params.search + '%',
    orderBy: [
      {
        column: getAuthUser()?.value?.language?.toLowerCase() === 'en'
          ? 'NAME_EN'
          : getAuthUser()?.value?.language?.toLowerCase() === 'es'
            ? 'NAME_ES'
            : getAuthUser()?.value?.language?.toLowerCase() === 'fr'
              ? 'NAME_FR'
              : getAuthUser()?.value?.language?.toLowerCase() === 'de'
                ? 'NAME_DE'
                : getAuthUser()?.value?.language?.toLowerCase() === 'sv'
                  ? 'NAME_SV'
                  : 'NAME_AR',
        order: params.sortDesc ? 'DESC' : 'ASC'
      }
    ],
    first: 100,
    page: 1
  }

  const result = query(apolloQuery, variables, { fetchPolicy: 'network-only' })

  return result
}

export async function searchAccommodationQualificationsCb(queryString: any, cb: (arg0: any) => void) {
  const { $showError, $sentry } = useNuxtApp()
  try {
    const results = await accommodationQualificationsAutocomplete({ search: queryString })
    cb(results?.getAllAccommodationQualifications?.data ?? [])
  } catch (err) {
    $showError(err)
    $sentry(err, 'searchAccommodationQualificationsCb')
  }
}

export function getSearchAccommodationQualifications(entityAccommodation: any, entityAccommodations: any) {
  if (firstTime.value === true) {
    firstTime.value = false
    searchAccommodationQualifications('', entityAccommodation, entityAccommodations)
  }

  if (entityAccommodation && !accommodationQualifications?.value?.find((k: any) => k.value === entityAccommodation.uuid)) {
    accommodationQualifications?.value?.push({
      label: getAuthUser()?.value?.language?.toLowerCase() === 'en'
          ? entityAccommodation.name_en
          : getAuthUser()?.value?.language?.toLowerCase() === 'es'
            ? entityAccommodation.name_es
            : getAuthUser()?.value?.language?.toLowerCase() === 'fr'
              ? entityAccommodation.name_fr
              : getAuthUser()?.value?.language?.toLowerCase() === 'de'
                ? entityAccommodation.name_de
                : getAuthUser()?.value?.language?.toLowerCase() === 'sv'
                  ? entityAccommodation.name_sv
                  : getAuthUser()?.value?.language?.toLowerCase() === 'ar'
                    ? entityAccommodation.name_ar
                    : entityAccommodation.name_en,
      value: entityAccommodation.uuid
    })
  }

  return getGroupedArray(accommodationQualifications?.value) ?? []
}

export async function searchAccommodationQualifications(queryString: any, entityAccommodation: any, entityAccommodations: any[], refresh = false) {
  const { $showError, $sentry } = useNuxtApp()
  try {
    const results = await accommodationQualificationsAutocomplete({ search: queryString })
    const remoteAccommodationQualifications = results?.getAllAccommodationQualifications?.data?.map((i: any) => {
      return {
        label: getAuthUser()?.value?.language?.toLowerCase() === 'en'
          ? i.name_en
          : getAuthUser()?.value?.language?.toLowerCase() === 'es'
            ? i.name_es
            : getAuthUser()?.value?.language?.toLowerCase() === 'fr'
              ? i.name_fr
              : getAuthUser()?.value?.language?.toLowerCase() === 'de'
                ? i.name_de
                : getAuthUser()?.value?.language?.toLowerCase() === 'sv'
                  ? i.name_sv
                  : getAuthUser()?.value?.language?.toLowerCase() === 'ar'
                    ? i.name_ar
                    : i.name_en,
        value: i.uuid
      }
    }) ?? []
    const accommodationsToAdd: { label: any; value: any }[] = []
    entityAccommodations?.forEach((j: any) => {
      if (!remoteAccommodationQualifications.find((k: any) => k.value === j.uuid)) {
        accommodationsToAdd.push({
          label: getAuthUser()?.value?.language?.toLowerCase() === 'en'
            ? j.name_en
            : getAuthUser()?.value?.language?.toLowerCase() === 'es'
              ? j.name_es
              : getAuthUser()?.value?.language?.toLowerCase() === 'fr'
                ? j.name_fr
                : getAuthUser()?.value?.language?.toLowerCase() === 'de'
                  ? j.name_de
                  : getAuthUser()?.value?.language?.toLowerCase() === 'sv'
                    ? j.name_sv
                    : getAuthUser()?.value?.language?.toLowerCase() === 'ar'
                      ? j.name_ar
                      : j.name_en,
          value: j.uuid
        })
      }
    })
    if (entityAccommodation && !remoteAccommodationQualifications.find((k: any) => k.value === entityAccommodation.uuid)) {
      accommodationsToAdd.push({
        label: getAuthUser()?.value?.language?.toLowerCase() === 'en'
          ? entityAccommodation.name_en
          : getAuthUser()?.value?.language?.toLowerCase() === 'es'
            ? entityAccommodation.name_es
            : getAuthUser()?.value?.language?.toLowerCase() === 'fr'
              ? entityAccommodation.name_fr
              : getAuthUser()?.value?.language?.toLowerCase() === 'de'
                ? entityAccommodation.name_de
                : getAuthUser()?.value?.language?.toLowerCase() === 'sv'
                  ? entityAccommodation.name_sv
                  : getAuthUser()?.value?.language?.toLowerCase() === 'ar'
                    ? entityAccommodation.name_ar
                    : entityAccommodation.name_en,
        value: entityAccommodation.uuid
      })
    }
    if (refresh) {
      key.value = 0
    }
    if (key.value === 0) {
      key.value = setCustomKeyVariable()
    }
    accommodationQualifications.value = [...remoteAccommodationQualifications, ...accommodationsToAdd]
    firstTime.value = false

    if (entityAccommodation) {
      getSearchAccommodationQualifications(entityAccommodation, entityAccommodations)
    }
  } catch (err) {
    $showError(err)
    $sentry(err, 'searchAccommodationQualifications')
  }
}

export function getAccommodationQualificationKey() {
  return key.value
}

export function initialAccommodationQualifications() {
  key.value = 0
  firstTime.value = true
  accommodationQualifications.value = []
}

const determineKeywords = (data) => {
  const keywordsSet = new Set();
  data.forEach(element => {
    const words = element.label.split(' ');
    words.forEach(word => {
      if (!isNaN(word)) return; // Ignore numbers
      keywordsSet.add(word);
    });
  });
  return Array.from(keywordsSet);
};

const determineGroup = (element, keywords) => {
  for (const keyword of keywords) {
    if (element.label.includes(keyword)) {
      return keyword;
    }
  }
  return null;
};

const getGroupedArray = (data) => {
  const keywords = determineKeywords(data);

  const groupedData = {};
  data.forEach(element => {
    const group = determineGroup(element, keywords);
    if (!groupedData[group]) {
      groupedData[group] = [];
    }
    groupedData[group].push(element);
  });

  const groupedArray = Object.values(groupedData);

  return groupedArray.flat()
}